import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _regeneratorRuntime from "@babel/runtime/regenerator";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import PropTypes from 'prop-types';
import { createRef, useContext, useEffect, useState } from 'react';
import { CART_URLS } from '../../apps/cart/constants';
import { COMING_SOON, DETAILS, NONE, SELECT, SIZE_DOWN, SIZE_UP, SOLD_OUT, TECHNICAL_DETAILS } from './constants';
import { fetchError } from '../cart/utils';
import { getPublishedStyledWithItems, scrollToReviews } from './utils';
import { getUrgencyMessage, getSizeUrgencyMessage, getWaitlistMessage } from './utils';
import { JSON_HEADERS, POST_HEADERS } from '../../config/constants';
import { OVContext } from '../../utils/OVContext';
import { ProductContext } from './context';
import { retailAddToCart, retailDetailPageView } from '../../utils/retail';
import { trackAddToCart } from '../../utils/cart/addTracker';
import Assets from './Assets';
import Breadcrumbs from '../../components/Breadcrumbs';
import css from './Product.module.scss';
import ErrorBoundary from '../../components/ErrorBoundary';
import ExpandCollapse from './ExpandCollapse';
import Instagrams from './Instagrams';
import NotForSale from './NotForSale';
import QuestionsAndReviews from './QuestionsAndReviews';
import RecommendationsAI from '../../components/RecommendationsAI';
import SideBar from './SideBar';
import SoldOut from './SoldOut';
import StyledWith from './StyledWith';
import Tabs from './Tabs';
import TechnicalDetails from './TechnicalDetails';
import WaitlistForm from './WaitlistForm';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { createElement as _createElement } from "react";

var Product = function Product(_ref) {
  var colorways = _ref.colorways,
      forSale = _ref.forSale,
      product = _ref.product,
      initialSizeSelected = _ref.sizeSelected,
      swatches = _ref.swatches,
      productKnowledgeSet = _ref.productKnowledgeSet,
      variant = _ref.variant;
  var _window = window,
      OV = _window.OV;
  var formRef = /*#__PURE__*/createRef(null);

  var _useContext = useContext(OVContext),
      mobileMedium = _useContext.mobileMedium; // state hooks


  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      mounted = _useState2[0],
      setMounted = _useState2[1];

  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      submitting = _useState4[0],
      setSubmitting = _useState4[1];

  var _useState5 = useState(colorways.find(function (_ref2) {
    var cwId = _ref2.id;
    return cwId === variant.colorway.id;
  }) || colorways[0]),
      _useState6 = _slicedToArray(_useState5, 2),
      colorway = _useState6[0],
      setColorway = _useState6[1];

  var _useState7 = useState(variant ? variant.size : ''),
      _useState8 = _slicedToArray(_useState7, 2),
      currentSize = _useState8[0],
      setCurrentSize = _useState8[1];

  var _useState9 = useState(initialSizeSelected),
      _useState10 = _slicedToArray(_useState9, 2),
      sizeSelected = _useState10[0],
      setSizeSelected = _useState10[1];

  var _useState11 = useState(false),
      _useState12 = _slicedToArray(_useState11, 2),
      showWaitlist = _useState12[0],
      setShowWaitlist = _useState12[1];

  var _useState13 = useState(false),
      _useState14 = _slicedToArray(_useState13, 2),
      showAskQuestion = _useState14[0],
      setShowAskQuestion = _useState14[1];

  useEffect(function () {
    setMounted(true);
  }, []); // root level data

  var attributeGroups = product.attributeGroups,
      breadcrumbs = product.breadcrumbs,
      fabric = product.fabric,
      pdp = product.pdp,
      title = product.title,
      productProperties = product.properties,
      split = product.split,
      soldOutStatus = product.soldOutStatus;
  var handle = colorway.handle,
      assetsEdges = colorway.assets.edges,
      styledWith = colorway.styledWith,
      currentColor = colorway.title,
      variantStacks = colorway.variantStacks,
      waitlistType = colorway.waitlistType,
      waitlistTypeDisplay = colorway.waitlistTypeDisplay; // pdp data

  var careInstructions = pdp.careInstructions,
      instagrams = pdp.instagrams,
      subTitle = pdp.subtitle,
      relatedCta = pdp.relatedCta,
      relatedUrl = pdp.relatedUrl; // properties data

  var description = productProperties.description,
      fitRatingAvg = productProperties.fitRatingAvg,
      gender = productProperties.gender,
      rating = productProperties.ratingAvg,
      recommendation = productProperties.recommendation,
      reviewable = productProperties.reviewable,
      reviewCount = productProperties.reviewCount,
      sizeComponent = productProperties.sizeComponent,
      sizeGuide = productProperties.sizeGuide,
      sizingRecommendation = productProperties.sizingRecommendation;
  var oneSize = variantStacks.length === 1;
  var displayWaitlist = waitlistType !== NONE;
  var comingSoon = waitlistType === COMING_SOON; // variant data

  var variants = variantStacks.map(function (vs) {
    return _objectSpread(_objectSpread({}, vs), vs.variant);
  });
  var userSelectedSize = sizeSelected && currentSize !== '';
  var firstVariant = variants[0];
  var currentVariant = userSelectedSize ? variants.find(function (vari) {
    return vari.size === currentSize;
  }) || firstVariant : firstVariant;
  var compareAtPrice = currentVariant.compareAtPrice,
      finalSale = currentVariant.finalSale,
      inventoryQuantity = currentVariant.inventoryQuantity,
      price = currentVariant.price,
      variantId = currentVariant.shopifyId,
      sku = currentVariant.sku; // auto-scroll to #reviews if hash present

  useEffect(function () {
    var hash = window.location.hash || '';

    if (hash && (hash === '#reviews' || hash === '#questions')) {
      scrollToReviews();
    }
  }, []);
  useEffect(function () {
    var _window2 = window,
        history = _window2.history,
        location = _window2.location;
    var urlParams = new URLSearchParams(location.search);
    var currentVariantId = parseInt(urlParams.get('variant') || 0, 10); // replace history state

    if (location.pathname === 'blank') {
      return;
    }

    if (currentVariant) {
      urlParams.set('variant', currentVariant.shopifyId);
    }

    if (sizeSelected) {
      urlParams.set('size', 1);
    } else {
      urlParams.delete('size');
    }

    var pathname = split ? "/products/".concat(handle) : location.pathname;
    var newURL = "".concat(pathname, "?").concat(urlParams.toString()).concat(mounted ? '' : window.location.hash);
    var newVariantId = parseInt(currentVariant.shopifyId, 10);

    if (!mounted || newVariantId !== currentVariantId) {
      // Track the view of the variant if we have a different id
      retailDetailPageView({
        variantId: currentVariant.shopifyId
      });
    }

    history.replaceState({}, '', newURL);
  }, [currentVariant, handle, mounted, sizeSelected, OV, split]); // assets

  var assets = [];
  var technicalDetails = [];
  assetsEdges.forEach(function (_ref3) {
    var node = _ref3.node;
    var fullWidth = node.fullWidth,
        nodeImage = node.image,
        type = node.type,
        video = node.video;
    var image = video ? video.poster : nodeImage;

    if (type === TECHNICAL_DETAILS) {
      technicalDetails.push(node);
    }

    assets.push(_objectSpread(_objectSpread({}, node), {}, {
      image: _objectSpread(_objectSpread({}, image), {}, {
        alt: "".concat(title, " in the color ").concat(currentColor, " - ").concat(image.alt)
      }),
      isTechnicalDetail: type === TECHNICAL_DETAILS,
      isTextAsset: type === DETAILS && image.description,
      fullWidth: fullWidth || type === DETAILS && !image.description || assetsEdges.length === 1
    }));
  }); // Hack this so the image doesn't get pushed to the bottom on mobile.
  // The whole PDP assets should probably get re-worked as it's a little overly complicated :(

  if (mobileMedium && assets.length === 1) {
    assets[0].fullWidth = false;
  }

  var soldOutSizes = [];
  var sizes = variantStacks.map(function (_ref4) {
    var size = _ref4.size,
        vari = _ref4.variant;
    var inventory = vari.inventoryQuantity;

    if (!vari.available) {
      var shouldShowWaitlist = inventory <= 0 && displayWaitlist;
      var optMessage = getWaitlistMessage({
        size: size,
        inventory: inventory,
        waitlistType: waitlistType
      });
      var optAriaLabel = "Size ".concat(size, " ").concat(optMessage);
      var onClick = shouldShowWaitlist ? function () {
        return setShowWaitlist(true);
      } : false;
      soldOutSizes.push(vari);
      return {
        disabled: true,
        label: size,
        value: size,
        onClick: onClick,
        optAriaLabel: optAriaLabel,
        optMessage: optMessage
      };
    }

    return {
      label: size,
      value: size,
      // Keep size-message consistent with LOW_INVENTORY_QUANTITY (don't use getInventoryMessage)
      optMessage: getSizeUrgencyMessage(inventory)
    };
  });
  var available = userSelectedSize ? currentVariant.available : colorway.available;
  var publishedStyledWith = getPublishedStyledWithItems(styledWith);
  var hasStyledWith = publishedStyledWith.length > 0;
  var styledWithElement = hasStyledWith ? /*#__PURE__*/_jsx(StyledWith, {
    styledWith: publishedStyledWith
  }) : false;

  var expandProductKnowledgeSet = _toConsumableArray(productKnowledgeSet);

  if (hasStyledWith) {
    expandProductKnowledgeSet.push({
      title: 'Styled With',
      child: styledWithElement
    });
  } // Manually set (EMS) status based on returning product, not inventory.


  if (soldOutStatus === SOLD_OUT) {
    return /*#__PURE__*/_jsx(SoldOut, _objectSpread(_objectSpread({}, product), {}, {
      rating: productProperties.ratingAvg,
      variantId: variantId
    }));
  } // COLORWAY MESSAGING - based on percentage of sold out sizes on colorway.


  var urgencyMessage = getUrgencyMessage({
    comingSoon: comingSoon,
    sizes: sizes,
    soldOutSizes: soldOutSizes
  });
  var displayRating = reviewCount > 0;

  if (!forSale) {
    return /*#__PURE__*/_jsx(NotForSale, _objectSpread(_objectSpread({}, product), {}, {
      assets: assets,
      instagrams: instagrams,
      shopifyId: product.shopifyId,
      sizeGuide: sizeGuide,
      rating: rating,
      variantId: variantId
    }));
  }

  var onSizeSelect = function onSizeSelect(size) {
    setCurrentSize("".concat(size));
    setSizeSelected(true);
  }; // const onSizeUnselect = () => {
  //   setCurrentSize('');
  //   setSizeSelected(false);
  // };


  var submit = /*#__PURE__*/function () {
    var _ref5 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
      var body, errorType, request, cartRequest, cartResponse;
      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) {
          switch (_context.prev = _context.next) {
            case 0:
              trackAddToCart({
                variantId: variantId,
                title: title,
                price: price
              });
              body = {
                id: variantId,
                properties: {
                  emsProductGid: product.id,
                  emsColorwayGid: colorway.id
                },
                quantity: 1
              };
              errorType = 'adding';
              _context.prev = 3;
              _context.next = 6;
              return fetch(CART_URLS.ADD, _objectSpread(_objectSpread({}, POST_HEADERS), {}, {
                body: JSON.stringify(body),
                credentials: 'same-origin'
              }));

            case 6:
              request = _context.sent;
              _context.next = 9;
              return request.json();

            case 9:
              _context.next = 11;
              return fetch(CART_URLS.GET, JSON_HEADERS);

            case 11:
              cartRequest = _context.sent;
              _context.next = 14;
              return cartRequest.json();

            case 14:
              cartResponse = _context.sent;
              retailAddToCart({
                cartToken: cartResponse.token,
                variantId: variantId
              });
              window.location.assign('/cart');
              _context.next = 23;
              break;

            case 19:
              _context.prev = 19;
              _context.t0 = _context["catch"](3);
              console.error(_context.t0);
              fetchError(errorType, _context.t0);

            case 23:
              setSubmitting(false);

            case 24:
            case "end":
              return _context.stop();
          }
        }
      }, _callee, null, [[3, 19]]);
    }));

    return function submit() {
      return _ref5.apply(this, arguments);
    };
  }();

  var addToBag = function addToBag() {
    if (available) {
      setSubmitting(true);
      submit();
    }
  }; // const urlParams = new URLSearchParams(window.location.search);
  // // replace history state
  // if (location.pathname !== 'blank') {
  //   if (currentVariant) {
  //     urlParams.set('variant', currentVariant.shopifyId);
  //   }
  //   if (sizeSelected) {
  //     urlParams.set('size', 1);
  //   } else {
  //     urlParams.delete('size');
  //   }
  //   const newURL = `${location.pathname}?${urlParams.toString()}${
  //     mounted ? '' : window.location.hash
  //   }`;
  //   history.replaceState({}, '', newURL);
  // }


  var onColorwaySelect = function onColorwaySelect(newColorwayId) {
    setColorway(colorways.find(function (clrwy) {
      return clrwy.id === newColorwayId;
    }));
  }; // internal team function, displays current sku


  var showSKU = function showSKU() {
    if (!OV) {
      return false;
    }

    var CUSTOMER = OV.CUSTOMER;
    var loggedIn = CUSTOMER.loggedIn,
        teamOV = CUSTOMER.teamOV;
    return (oneSize || sizeSelected) && loggedIn && teamOV;
  }; // internal team function, updates nav for product urls


  if (showSKU()) {
    _toConsumableArray(document.querySelectorAll('.nav-product-sku')).forEach(function (link) {
      link.href = "".concat(link.getAttribute('data-url')).concat(sku);
      link.text = "NetSuite: ".concat(colorway.color, "/").concat(currentVariant.size, ": ").concat(sku);
      link.style.display = 'block';
    });
  }

  var lightBoxSubtitle = /*#__PURE__*/_jsx("em", {
    children: "in ".concat(currentColor)
  });

  var submitReviewURL = "/pages/submit-a-review?product_id=".concat(product.shopifyId);
  return /*#__PURE__*/_jsx(ProductContext.Provider, {
    value: {
      careInstructions: careInstructions,
      colorway: colorway,
      fabric: fabric,
      fitRatingAvg: fitRatingAvg,
      reviewCount: reviewCount,
      setShowAskQuestion: setShowAskQuestion,
      showAskQuestion: showAskQuestion
    },
    children: /*#__PURE__*/_jsxs("div", {
      className: css.root,
      itemScope: "",
      itemType: "http://schema.org/Product",
      children: [/*#__PURE__*/_jsx(Breadcrumbs, {
        breadcrumbsList: breadcrumbs,
        pageTitle: title
      }), /*#__PURE__*/_jsx(ErrorBoundary, {
        children: /*#__PURE__*/_jsxs("div", {
          className: css.productCore,
          children: [/*#__PURE__*/_jsx(Assets, {
            assets: assets,
            description: description,
            subtitle: lightBoxSubtitle,
            title: title
          }), /*#__PURE__*/_jsx(SideBar, {
            addToBag: addToBag,
            assets: assets,
            attributeGroups: attributeGroups,
            available: available,
            color: currentColor,
            colorwayId: colorway.id,
            comingSoon: comingSoon,
            compareAtPrice: compareAtPrice,
            currentSize: currentSize || currentVariant.size,
            displayRating: displayRating,
            displayWaitlist: displayWaitlist,
            finalSale: finalSale,
            inventoryQuantity: inventoryQuantity,
            onColorSelect: onColorwaySelect,
            oneSize: oneSize,
            onSizeSelect: onSizeSelect,
            price: price,
            productId: product.id,
            rating: rating,
            relatedCta: relatedCta,
            relatedUrl: relatedUrl,
            reviewable: reviewable,
            reviewCount: reviewCount,
            shopifyId: product.shopifyId,
            showWaitlist: function showWaitlist() {
              return setShowWaitlist(true);
            },
            sizeDown: sizingRecommendation === SIZE_DOWN,
            sizeDropdown: sizeComponent === SELECT,
            sizeGuide: sizeGuide,
            sizes: sizes,
            sizeSelected: sizeSelected,
            sizeUp: sizingRecommendation === SIZE_UP,
            styledWith: styledWithElement,
            submitReviewURL: submitReviewURL,
            submitting: submitting,
            subTitle: subTitle,
            swatches: swatches,
            swatchNum: swatches.length,
            title: title,
            urgencyMessage: urgencyMessage,
            variant: currentVariant,
            waitlistTypeDisplay: waitlistTypeDisplay
          })]
        })
      }), showSKU() && /*#__PURE__*/_jsx("div", {
        className: css.sku,
        children: sku
      }), mobileMedium && /*#__PURE__*/_jsx(ExpandCollapse, {
        productKnowledgeSet: productKnowledgeSet
      }), hasStyledWith && /*#__PURE__*/_jsx("div", {
        className: css.mobileStyledWith,
        children: styledWithElement
      }), productKnowledgeSet.length > 0 && /*#__PURE__*/_jsx(Tabs, {
        productKnowledgeSet: productKnowledgeSet
      }), technicalDetails.length > 0 && /*#__PURE__*/_jsx(TechnicalDetails, {
        productTitle: product.title,
        technicalDetails: technicalDetails
      }), /*#__PURE__*/_jsx("div", {
        className: css.mobileAssets,
        children: /*#__PURE__*/_jsx(Assets, {
          allowFullWidth: true,
          assets: assets,
          textAssetsOnly: true,
          description: description,
          subtitle: lightBoxSubtitle,
          title: title
        })
      }), instagrams && /*#__PURE__*/_jsx(Instagrams, {
        items: instagrams,
        label: product.title
      }), /*#__PURE__*/_jsx(WaitlistForm, {
        comingSoon: comingSoon,
        displayTitle: waitlistTypeDisplay,
        gender: gender,
        hideWaitlist: function hideWaitlist() {
          return setShowWaitlist(false);
        },
        on: showWaitlist,
        title: title,
        variant: variantId
      }), reviewable && /*#__PURE__*/_jsx(QuestionsAndReviews, _objectSpread(_objectSpread({}, productProperties), {}, {
        product: product,
        variantId: variantId
      })), recommendation && /*#__PURE__*/_createElement(RecommendationsAI, _objectSpread(_objectSpread({}, recommendation), {}, {
        key: "recommendations-".concat(colorway.id),
        variantIds: [variantId]
      })), /*#__PURE__*/_jsxs("form", {
        method: "post",
        action: "/cart/add",
        className: css.form,
        ref: formRef,
        children: [/*#__PURE__*/_jsx("input", {
          type: "hidden",
          name: "id",
          value: variantId
        }), /*#__PURE__*/_jsx("input", {
          type: "hidden",
          name: "quantity",
          value: "1"
        })]
      })]
    })
  });
};

Product.defaultProps = {
  attributeGroups: null,
  sizeSelected: false
};
Product.propTypes = {
  colorways: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  forSale: PropTypes.bool.isRequired,
  product: PropTypes.shape({
    attributeGroups: PropTypes.shape(),
    breadcrumbs: PropTypes.arrayOf(PropTypes.shape({
      title: PropTypes.string.isRequired,
      url: PropTypes.string
    })),
    fabric: PropTypes.shape({}),
    id: PropTypes.string,
    pdp: PropTypes.shape({
      careInstructions: PropTypes.shape({}),
      instagrams: PropTypes.arrayOf(PropTypes.shape()),
      relatedCta: PropTypes.string,
      relatedUrl: PropTypes.string,
      subtitle: PropTypes.string
    }),
    properties: PropTypes.shape({
      description: PropTypes.string,
      fitRatingAvg: PropTypes.number,
      gender: PropTypes.string,
      ratingAvg: PropTypes.number,
      recommendation: PropTypes.shape({}),
      reviewable: PropTypes.bool,
      reviewCount: PropTypes.number,
      sizeComponent: PropTypes.string,
      sizeGuide: PropTypes.string,
      sizingRecommendation: PropTypes.string
    }),
    soldOutStatus: PropTypes.string,
    split: PropTypes.bool,
    title: PropTypes.string,
    shopifyId: PropTypes.number
  }).isRequired,
  sizeSelected: PropTypes.bool,
  swatches: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  productKnowledgeSet: PropTypes.arrayOf(PropTypes.shape({})),
  variant: PropTypes.shape({
    colorway: PropTypes.shape({
      id: PropTypes.string
    }),
    size: PropTypes.string
  }).isRequired
};
export default Product;